/**
 * Page imports + Suspense wrapper shared across the three shell trees.
 *
 * Code-splitting strategy (Round 5 PR 5.1, kept in PR 14 cleanup):
 * customer-facing happy path (Landing, QuoteDetail, Cart, Checkout,
 * OrderThanks, PlatformLanding) stays statically imported so the
 * first paint isn't gated on a second network round-trip.
 * Everything else gets ``React.lazy`` + a single Suspense fallback,
 * so the marketing visitor doesn't pay for the admin chunks they'll
 * never load.
 *
 * Vite dedupes by module identity, so importing the same lazy ref
 * from multiple shell files still yields one shared chunk.
 */

import { lazy, Suspense, type ReactNode } from "react";

// Eager — initial paint critical-path.
import Landing from "@/pages/Landing";
import NotFound from "@/pages/NotFound";
import PlatformLanding from "@/pages/PlatformLanding";

// Eager — first-touch buyer flow (upload → quote → cart → checkout
// → thanks). Lazy-loading these would put a Suspense fallback onto
// the most important conversion path.
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import OrderThanks from "@/pages/OrderThanks";
import QuoteDetail from "@/pages/QuoteDetail";

export {
  Cart,
  Checkout,
  Landing,
  NotFound,
  OrderThanks,
  PlatformLanding,
  QuoteDetail,
};

// --- Lazy pages -------------------------------------------------------

export const Account = lazy(() => import("@/pages/Account"));
export const AccountAddresses = lazy(
  () => import("@/pages/AccountAddresses"),
);
export const AccountEmailChangeConfirm = lazy(
  () => import("@/pages/AccountEmailChangeConfirm"),
);
export const AuthVerifyEmail = lazy(
  () => import("@/pages/AuthVerifyEmail"),
);
export const Approvals = lazy(() => import("@/pages/Approvals"));
export const Contact = lazy(() => import("@/pages/Contact"));
export const PlatformTryout = lazy(() => import("@/pages/PlatformTryout"));
export const CustomerOrgOnboarding = lazy(
  () => import("@/pages/CustomerOrgOnboarding"),
);
export const ForBusiness = lazy(() => import("@/pages/ForBusiness"));
export const HowItWorks = lazy(() => import("@/pages/HowItWorks"));
// Same path, different shell: the tenant tree mounts ``HowItWorks``
// (written for the buyer), the platform tree this one (written for the
// shop owner evaluating the platform). See KAN-717.
export const PlatformHowItWorks = lazy(
  () => import("@/pages/PlatformHowItWorks"),
);
export const InvitationsAccept = lazy(
  () => import("@/pages/InvitationsAccept"),
);
export const LegalPage = lazy(() =>
  import("@/pages/LegalPage").then((m) => ({ default: m.LegalPage })),
);
export const Materials = lazy(() => import("@/pages/Materials"));
export const MyProjectDetail = lazy(() => import("@/pages/MyProjectDetail"));
export const MyProjectItemConfigure = lazy(
  () => import("@/pages/MyProjectItemConfigure"),
);
export const MyProjects = lazy(() => import("@/pages/MyProjects"));
export const OrderHistory = lazy(() => import("@/pages/OrderHistory"));
export const OrgsVerify = lazy(() => import("@/pages/OrgsVerify"));
export const SsoLinkConfirm = lazy(() => import("@/pages/SsoLinkConfirm"));
export const PasswordResetConfirm = lazy(
  () => import("@/pages/PasswordResetConfirm"),
);
export const PasswordResetRequest = lazy(
  () => import("@/pages/PasswordResetRequest"),
);
export const TwoFactorBootstrap = lazy(
  () => import("@/pages/TwoFactorBootstrap"),
);
export const Pricing = lazy(() => import("@/pages/Pricing"));
export const PricingPlans = lazy(() => import("@/pages/PricingPlans"));
export const Security = lazy(() => import("@/pages/Security"));
export const VsMarketplaces = lazy(() => import("@/pages/VsMarketplaces"));
// Sekce „Co umíme" (KAN-1462). Rozcestník + jedna stránka na soubor;
// další stránky sekce přibývají vedle, ne dovnitř.
export const FeaturesHub = lazy(() => import("@/pages/features/FeaturesHub"));
export const FeaturesQuoting = lazy(
  () => import("@/pages/features/FeaturesQuoting"),
);
export const FeaturesWarehouse = lazy(
  () => import("@/pages/features/FeaturesWarehouse"),
);
export const FeaturesProduction = lazy(
  () => import("@/pages/features/FeaturesProduction"),
);
export const FeaturesB2b = lazy(
  () => import("@/pages/features/FeaturesB2b"),
);
export const FeaturesInvoicing = lazy(
  () => import("@/pages/features/FeaturesInvoicing"),
);
export const FeaturesIntegrations = lazy(
  () => import("@/pages/features/FeaturesIntegrations"),
);
export const OnboardingStart = lazy(() => import("@/pages/OnboardingStart"));
export const ProviderOnboarding = lazy(
  () => import("@/pages/ProviderOnboarding"),
);
// KAN-35 — konec prokliku na ukázkovém obchodě platformy.
export const DemoShopEnd = lazy(() => import("@/pages/DemoShopEnd"));
export const PublicOrg = lazy(() => import("@/pages/PublicOrg"));
export const PublicQuote = lazy(() => import("@/pages/PublicQuote"));
export const QualityReport = lazy(() => import("@/pages/QualityReport"));
export const QuoteBatch = lazy(() => import("@/pages/QuoteBatch"));
export const RefundAccount = lazy(() => import("@/pages/RefundAccount"));
export const ServiceLanding = lazy(() => import("@/pages/ServiceLanding"));
export const SignIn = lazy(() => import("@/pages/SignIn"));
export const SignUpShop = lazy(() => import("@/pages/SignUpShop"));
export const SignUpTeam = lazy(() => import("@/pages/SignUpTeam"));
export const SignUpTeamFinalize = lazy(
  () => import("@/pages/SignUpTeamFinalize"),
);
export const Subprocessors = lazy(() => import("@/pages/Subprocessors"));
export const Track = lazy(() => import("@/pages/Track"));

// Buyer routes — entirely off the customer-facing path.
export const BuyerAddressBook = lazy(
  () => import("@/pages/buyer/BuyerAddressBook"),
);
export const BuyerCapacityPurchase = lazy(
  () => import("@/pages/buyer/BuyerCapacityPurchase"),
);
export const BuyerDashboard = lazy(
  () => import("@/pages/buyer/BuyerDashboard"),
);
export const BuyerInvoices = lazy(() => import("@/pages/buyer/BuyerInvoices"));
export const BuyerReports = lazy(() => import("@/pages/buyer/BuyerReports"));

// Admin pages.
export const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
export const AdminApiTokens = lazy(
  () => import("@/pages/admin/AdminApiTokens"),
);
export const AdminApprovals = lazy(
  () => import("@/pages/admin/AdminApprovals"),
);
export const AdminAuditLog = lazy(() => import("@/pages/admin/AdminAuditLog"));
export const AdminBilling = lazy(() => import("@/pages/admin/AdminBilling"));
export const AdminBusinessInfo = lazy(
  () => import("@/pages/admin/AdminBusinessInfo"),
);
export const AdminCompanyProfile = lazy(
  () => import("@/pages/admin/AdminCompanyProfile"),
);
export const AdminPlatformQuoteSettings = lazy(
  () => import("@/pages/admin/AdminPlatformQuoteSettings"),
);
export const AdminTenantQuoteSettings = lazy(
  () => import("@/pages/admin/AdminTenantQuoteSettings"),
);
// AdminSkillMatrix („Matice dovedností") smazána — celý per-node detail
// (matice / modul / sazby / public web) žije v drawer inspektoru mapy
// výroby (``NodeDetailTabs``). ``/admin/skill-matrix`` redirectuje.
export const AdminCarbon = lazy(() => import("@/pages/admin/AdminCarbon"));
export const AdminCustomerDetail = lazy(
  () => import("@/pages/admin/AdminCustomerDetail"),
);
// PR10c: AdminCustomers smazán — list žije na centrálních
// `/admin/contacts`. Detail (`/customers/:userId`) drží
// AdminCustomerDetail.
export const AdminDataExport = lazy(
  () => import("@/pages/admin/AdminDataExport"),
);
export const AdminDataResidency = lazy(
  () => import("@/pages/admin/AdminDataResidency"),
);
export const AdminMail = lazy(() => import("@/pages/admin/mail/AdminMail"));
export const AdminContactInbox = lazy(
  () => import("@/pages/admin/AdminContactInbox"),
);
export const AdminContacts = lazy(
  () => import("@/pages/admin/AdminContacts"),
);
export const AdminDisputes = lazy(() => import("@/pages/admin/AdminDisputes"));
export const AdminSalesChannels = lazy(
  () => import("@/pages/admin/AdminSalesChannels"),
);
export const AdminFeatureSettings = lazy(
  () => import("@/pages/admin/AdminFeatureSettings"),
);
export const AdminPageVisibilityRules = lazy(
  () => import("@/pages/admin/AdminPageVisibilityRules"),
);
// KAN-1376: editor struktury menu — platformní výchozí i tenantní přepis.
export const AdminMenuLayout = lazy(
  () => import("@/pages/admin/menuLayout/AdminMenuLayout"),
);
// KAN-385: „Aplikace" a „Integrace" jsou jedna plocha. Obě staré cesty
// na ni přesměrovávají (viz TenantServiceShell).
export const AdminExtensions = lazy(
  () => import("@/pages/admin/AdminExtensions"),
);
export const AdminIntegrationsOverview = lazy(
  () => import("@/pages/admin/AdminIntegrationsOverview"),
);
// Sloučené „Slevy" + „Limity objednávek" (staré ``AdminDiscounts`` /
// ``AdminLimits`` jsou teď sekce téhle stránky).
export const AdminOrderRules = lazy(
  () => import("@/pages/admin/AdminOrderRules"),
);
export const AdminNewsletter = lazy(
  () => import("@/pages/admin/AdminNewsletter"),
);
export const AdminTemplates = lazy(
  () => import("@/pages/admin/AdminTemplates"),
);
export const AdminVatSettings = lazy(
  () => import("@/pages/admin/AdminVatSettings"),
);
export const AdminBaseCurrency = lazy(
  () => import("@/pages/admin/AdminBaseCurrency"),
);
export const AdminGroups = lazy(() => import("@/pages/admin/AdminGroups"));
// PR10a: AdminMembers smazán — nahradily ho centrální Kontakty.
export const DeveloperApps = lazy(
  () => import("@/pages/DeveloperApps"),
);
export const AdminAppHost = lazy(() => import("@/pages/admin/AdminAppHost"));
export const AdminPlatformApps = lazy(
  () => import("@/pages/admin/AdminPlatformApps"),
);
// PR10a: AdminTeam smazán — nahradily ho centrální Kontakty.
export const AdminBoards = lazy(
  () => import("@/pages/admin/AdminBoards"),
);
export const AdminOperations = lazy(
  () => import("@/pages/admin/AdminOperations"),
);
// KAN-2042 — osa „Dávky" Tabule; vlastní chunk jako ostatní osy.
export const ProductionJobsBoard = lazy(() =>
  import("@/components/admin/productionJobs/ProductionJobsBoard").then(
    (m) => ({ default: m.ProductionJobsBoard }),
  ),
);
export const AdminOrderAssembly = lazy(
  () => import("@/pages/admin/AdminOrderAssembly"),
);
export const AdminOrderDetail = lazy(
  () => import("@/pages/admin/AdminOrderDetail"),
);
export const AdminOrders = lazy(() => import("@/pages/admin/AdminOrders"));
export const AdminOverview = lazy(() => import("@/pages/admin/AdminOverview"));
export const AdminMyOrganizations = lazy(
  () => import("@/pages/admin/AdminMyOrganizations"),
);
export const AdminPartnerDetail = lazy(
  () => import("@/pages/admin/AdminPartnerDetail"),
);
export const AdminPartnerQueue = lazy(
  () => import("@/pages/admin/AdminPartnerQueue"),
);
export const AdminPartners = lazy(() => import("@/pages/admin/AdminPartners"));
export const AdminPipelineEditor = lazy(
  () => import("@/pages/admin/AdminPipelineEditor"),
);
export const AdminPlatformAuditLog = lazy(
  () => import("@/pages/admin/AdminPlatformAuditLog"),
);
export const AdminPlatformCompanyProfile = lazy(
  () => import("@/pages/admin/AdminPlatformCompanyProfile"),
);
// Jedna komponenta pro oba shelly — scope si bere z ``resolveShell()``.
export const AdminLegalDocuments = lazy(
  () => import("@/pages/admin/AdminLegalDocuments"),
);
export const AdminCapacityPricing = lazy(
  () => import("@/pages/admin/AdminCapacityPricing"),
);
// KAN-1365: kdo si přepsal kterou platformní výchozí hodnotu.
export const AdminSettingOverrides = lazy(
  () => import("@/pages/admin/AdminSettingOverrides"),
);
export const AdminChangelog = lazy(() => import("@/pages/admin/AdminChangelog"));
export const Changelog = lazy(() => import("@/pages/Changelog"));
export const ChangelogPost = lazy(() => import("@/pages/ChangelogPost"));
export const AdminPlatformGroupDetail = lazy(
  () => import("@/pages/admin/AdminPlatformGroupDetail"),
);
export const AdminPlatformGroups = lazy(
  () => import("@/pages/admin/AdminPlatformGroups"),
);
// „Jazyky a překlady" — DB-backed language registry CRUD + platform-shell
// translation editor (the central i18n management surface).
export const AdminPlatformLanguages = lazy(
  () => import("@/pages/admin/AdminPlatformLanguages"),
);
export const AdminPlatformOverview = lazy(
  () => import("@/pages/admin/AdminPlatformOverview"),
);
export const AdminPlatformLifecycleGraph = lazy(
  () => import("@/pages/admin/AdminPlatformLifecycleGraph"),
);
export const AdminPlatformProcesses = lazy(
  () => import("@/pages/admin/AdminPlatformProcesses"),
);
// Sloučená „Materiály" — kategorie materiálů + registr vlastností
// materiálů na jedné stránce (skládá obě původní komponenty).
export const AdminPlatformMaterials = lazy(
  () => import("@/pages/admin/AdminPlatformMaterials"),
);
// „Materiály" — platformní katalog materiálů. Vlastní stránka od
// 2026-09-12 (dřív druhá záložka „Kategorie položek"); oprávnění
// sdílí s ní.
export const AdminPlatformMaterialCatalog = lazy(
  () => import("@/pages/admin/AdminPlatformMaterialCatalog"),
);
// Artifact I/O kontrakty — ArtifactPropertyDefinition CRUD (slovník
// vlastností artefaktu/polotovaru pro kontrakty operací).
export const AdminPlatformArtifactProperties = lazy(
  () => import("@/pages/admin/AdminPlatformArtifactProperties"),
);
// "Vlastnosti strojů" — grouping of machine-config-schema properties across
// operations (mark which keys are the same so the union form asks once).
export const AdminPlatformMachineProperties = lazy(
  () => import("@/pages/admin/AdminPlatformMachineProperties"),
);
// „Katalog strojů" (KAN-1099) — the "stroj" building block a tenant picks
// from at start-up: base identity, config per operation, schedule, dimensions.
export const AdminPlatformMachines = lazy(
  () => import("@/pages/admin/AdminPlatformMachines"),
);
// Krok C.1 — tenant override editor (delta nad platform defaults).
export const AdminTenantMaterialProperties = lazy(
  () => import("@/pages/admin/AdminTenantMaterialProperties"),
);
export const AdminPlatformOperationCategories = lazy(
  () => import("@/pages/admin/AdminPlatformOperationCategories"),
);
// „Jednotky" — full-page overview + CRUD over the platform unit palette
// (PlatformUnitPreset), grouped by dimension to show conversion relationships.
export const AdminPlatformUnits = lazy(
  () => import("@/pages/admin/AdminPlatformUnits"),
);
// „Doprava a termíny" — platformní katalog dopravců, sazeb per země a
// výchozích úrovní dodání, ze kterého si tenant na onboardingu vybírá.
export const AdminPlatformShipping = lazy(
  () => import("@/pages/admin/AdminPlatformShipping"),
);
// „Země" — platformní katalog zemí (KAN-1449); nahradil seznam natvrdo v API.
export const AdminPlatformCountries = lazy(
  () => import("@/pages/admin/AdminPlatformCountries"),
);
// „Měny" — platformní katalog měn (KAN-1451); nahradil konstantu v API.
// KAN-1568: interní obchodní CRM platformy „Obchod".
export const AdminCrm = lazy(() => import("@/pages/admin/AdminCrm"));
export const AdminPlatformCurrencies = lazy(
  () => import("@/pages/admin/AdminPlatformCurrencies"),
);
export const AdminHelpAnnotations = lazy(
  () => import("@/pages/admin/AdminHelpAnnotations"),
);
export const AdminTenantDetail = lazy(
  () => import("@/pages/admin/AdminTenantDetail"),
);
export const AdminOperationGraph = lazy(
  () => import("@/pages/admin/AdminOperationGraph"),
);
export const AdminTenants = lazy(() => import("@/pages/admin/AdminTenants"));
export const AdminPlans = lazy(() => import("@/pages/admin/AdminPlans"));
export const AdminProduction = lazy(
  () => import("@/pages/admin/AdminProduction"),
);
export const AdminTenantOperations = lazy(
  () => import("@/pages/admin/AdminTenantOperations"),
);
export const AdminProductionSettings = lazy(
  () => import("@/pages/admin/AdminProductionSettings"),
);
export const AdminProviderCustomerDetail = lazy(
  () => import("@/pages/admin/AdminProviderCustomerDetail"),
);
// PR10a: AdminProviderCustomers smazán — nahradily ho centrální
// Kontakty. AdminProviderCustomerDetail (`/:customerOrgId`)
// zachován jako CRM detail.
export const AdminProviderRanking = lazy(
  () => import("@/pages/admin/AdminProviderRanking"),
);
export const AdminNumberSeries = lazy(
  () => import("@/pages/admin/AdminNumberSeries"),
);
export const AdminInvoices = lazy(() => import("@/pages/admin/AdminInvoices"));
export const AdminInvoiceNew = lazy(
  () => import("@/pages/admin/AdminInvoiceNew"),
);
export const AdminInvoiceDetail = lazy(
  () => import("@/pages/admin/AdminInvoiceDetail"),
);
export const AdminQuotes = lazy(() => import("@/pages/admin/AdminQuotes"));
export const AdminQuoteDetail = lazy(() =>
  import("@/pages/admin/AdminQuotes").then((m) => ({
    default: m.AdminQuoteDetail,
  })),
);
export const AdminQuoteNew = lazy(() =>
  import("@/pages/admin/quotes/AdminQuoteNew"),
);
export const AdminSamlSetup = lazy(
  () => import("@/pages/admin/AdminSamlSetup"),
);
export const AdminOidcSetup = lazy(
  () => import("@/pages/admin/AdminOidcSetup"),
);
export const AdminSandbox = lazy(() => import("@/pages/admin/AdminSandbox"));
export const AdminMaterialVariantDetail = lazy(
  () => import("@/pages/admin/AdminMaterialVariantDetail"),
);
export const AdminItems = lazy(() => import("@/pages/admin/AdminItems"));
// KAN-1071 — „Ceník operace". Bez položky v navigaci: vede sem odkaz
// z karty ceny položky a ze šuplíku uzlu.
export const AdminOperationRates = lazy(
  () => import("@/pages/admin/AdminOperationRates"),
);
// KAN-1072 — cenová laboratoř: rozpad ceny vlastních dílů, kde každé
// editovatelné číslo pojmenuje záznam, do kterého by se zapsalo.
export const AdminPricingLab = lazy(
  () => import("@/pages/admin/AdminPricingLab"),
);
// KAN-1352 — vstupy ceny: co cena operace potřebuje a obchod nemá, mimo průvodce.
export const AdminPricingInputs = lazy(
  () => import("@/pages/admin/AdminPricingInputs"),
);
// KAN-1073 — kalkulační schéma tenanta: jeden kalkulační list pro všechny
// operace. Řádky autoruje tenant; platforma přináší jen aritmetiku.
export const AdminCostingScheme = lazy(
  () => import("@/pages/admin/AdminCostingScheme"),
);
export const AdminItemDetail = lazy(
  () => import("@/pages/admin/AdminItemDetail"),
);
export const AdminStockMovements = lazy(
  () => import("@/pages/admin/AdminStockMovements"),
);
export const AdminPickList = lazy(
  () => import("@/pages/admin/AdminPickList"),
);
export const AdminStorageLocations = lazy(
  () => import("@/pages/admin/AdminStorageLocations"),
);
// KAN-473 — nabídka hotových dávek bez odběratele → naskladnění (KAN-311/312).
export const AdminFinishedStock = lazy(
  () => import("@/pages/admin/AdminFinishedStock"),
);
export const AdminStorageMap = lazy(
  () => import("@/pages/admin/AdminStorageMap"),
);
// Dílna (spec_shopfloor_module D1b) — terminál pracoviště s vlastním
// chrome (viz ShellFrame v App.tsx), přístupný i roli production.
export const AdminShopfloor = lazy(
  () => import("@/pages/admin/AdminShopfloor"),
);
// Mapa (KAN-28, M1) — střecha nad čočkami Proces a Hala.
export const AdminMap = lazy(() => import("@/pages/admin/AdminMap"));
export const ShopfloorLayout = lazy(
  () => import("@/pages/shopfloor/ShopfloorLayout"),
);
export const ShopfloorPlaces = lazy(
  () => import("@/pages/shopfloor/ShopfloorPlaces"),
);
// KAN-246 (M0) — Provoz jako seznam, přehled mistra.
export const ShopfloorOverview = lazy(
  () => import("@/pages/shopfloor/ShopfloorOverview"),
);
// KAN-2023 — expedice: k zabalení, k odeslání, k vyzvednutí.
export const ShopfloorFulfillment = lazy(
  () => import("@/pages/shopfloor/ShopfloorFulfillment"),
);
export const ShopfloorPlaceTasks = lazy(
  () => import("@/pages/shopfloor/ShopfloorPlaceTasks"),
);
export const ShopfloorMyTasks = lazy(
  () => import("@/pages/shopfloor/ShopfloorMyTasks"),
);
// KAN-1981 — „Moje pracoviště": fronta místa, u kterého tablet stojí.
export const ShopfloorStation = lazy(
  () => import("@/pages/shopfloor/ShopfloorStation"),
);
// KAN-1750 — tablet se páruje QR kódem nebo krátkým odkazem.
export const ShopfloorPair = lazy(() => import("@/pages/shopfloor/ShopfloorPair"));
// KAN-305 — rozebrání kontejneru se známým obsahem, per dítě.
export const ShopfloorContainerDisassembly = lazy(
  () => import("@/pages/shopfloor/ShopfloorContainerDisassembly"),
);
export const AdminSuppliers = lazy(
  () => import("@/pages/admin/AdminSuppliers"),
);
export const AdminSupplierDetail = lazy(
  () => import("@/pages/admin/AdminSupplierDetail"),
);
export const AdminPurchaseOrders = lazy(
  () => import("@/pages/admin/AdminPurchaseOrders"),
);
export const AdminPurchaseOrderDetail = lazy(
  () => import("@/pages/admin/AdminPurchaseOrderDetail"),
);
export const AdminMaterialBatches = lazy(
  () => import("@/pages/admin/AdminMaterialBatches"),
);
export const AdminTrace = lazy(() => import("@/pages/admin/AdminTrace"));
// AdminProcesses („Naše procesy") splynula do tabu „Modul" v node-detail
// draweru mapy výroby; lazy import + page komponenta smazána. Types
// extract žijí v ``@/types/adminProcesses``.
// H3 #6 PR-B — platform-admin review queue (cross-tenant).
export const PlatformAdminModuleAdapters = lazy(
  () => import("@/pages/admin/PlatformAdminModuleAdapters"),
);
export const AdminMachines = lazy(() => import("@/pages/admin/AdminMachines"));
export const AdminShipping = lazy(() => import("@/pages/admin/AdminShipping"));
export const AdminMarkets = lazy(() => import("@/pages/admin/AdminMarkets"));
export const AdminClaims = lazy(() => import("@/pages/admin/AdminClaims"));
export const AdminStripeConnect = lazy(
  () => import("@/pages/admin/AdminStripeConnect"),
);
export const AdminWebhooks = lazy(() => import("@/pages/admin/AdminWebhooks"));

/**
 * Suspense fallback rendered while a lazy chunk is in-flight.
 *
 * Intentionally minimal — every page renders its own loading state
 * once it's mounted, and a heavy fallback would flash for the small
 * window between chunk-arrival and first-render.
 */
function ChunkFallback(): JSX.Element {
  return (
    <div
      role="status"
      aria-label="loading"
      data-testid="route-chunk-loading"
      className="flex min-h-[40vh] items-center justify-center text-sm text-ink-3"
    >
      …
    </div>
  );
}

export function Lazy({ children }: { children: ReactNode }): JSX.Element {
  return <Suspense fallback={<ChunkFallback />}>{children}</Suspense>;
}
